<template>
  <div
    class="file-input-big"
    :class="{
      error: vErrors.length > 0 || isError,
      focused: isFocused,
      filled: !!selectedFile & !isFocused,
    }"
    v-click-outside="emitFocusOut"
  >
    <div
      class="file-input-big__wrapper"
      @dragenter.prevent="setFocused"
      @dragleave.prevent="setUnfocused"
      @drop.prevent="handleDrop"
    >
      <label class="file-input-big__el">
        <input
          type="file"
          ref="inputEl"
          @change="processInput"
          :accept="accept"
        />
        <span class="file-input-big__el-icon">
          <svg
            width="32"
            height="30"
            viewBox="0 0 32 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.1996 21.0005C20.1996 21.0005 25.1868 21.0005 25.6584 21.0005C28.2774 21.0005 30.3996 18.8783 30.3996 16.2599C30.3996 13.6415 28.2774 11.5187 25.6584 11.5187C25.6464 11.5187 25.6356 11.5205 25.6236 11.5205C25.6602 11.2241 25.6854 10.9241 25.6854 10.6181C25.6854 6.59212 22.422 3.32812 18.3954 3.32812C15.258 3.32812 12.5922 5.31353 11.5638 8.09273C10.9116 7.43933 10.0092 7.03432 9.01321 7.03432C7.02181 7.03432 5.40781 8.64833 5.40781 10.6391C5.40781 10.6901 5.41381 10.7393 5.41561 10.7897C3.21661 11.3975 1.59961 13.4069 1.59961 15.7991C1.59961 18.6719 3.92821 21.0005 6.80161 21.0005C7.29781 21.0005 11.7996 21.0005 11.7996 21.0005"
              stroke="#64748B"
              stroke-width="2"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19 24.5996L16 27.5996L13 24.5996"
              stroke="#64748B"
              stroke-width="2"
              stroke-miterlimit="10"
              stroke-linecap="round"
            />
            <path
              d="M16 15.5996V27.4004"
              stroke="#64748B"
              stroke-width="2"
              stroke-miterlimit="10"
              stroke-linecap="round"
            />
          </svg>
        </span>
        <span
          class="file-input-big__el-text"
          :title="selectedFile ? selectedFile.name : placeholder"
        >
          {{
            selectedFile && !isFocused
              ? selectedFile.name
              : translatedPlaceholder
          }}
        </span>
        <span class="file-input-big__el-hint" v-if="!selectedFile && hint">
          {{ translatedHint }}
        </span>
      </label>
    </div>

    <div class="file-input-big__hint" v-if="hint">
      {{ translatedHint }}
    </div>
    <div class="file-input-big__errors" v-if="vErrors.length > 0">
      <div class="file-input-big__error">{{ vErrors[0].$message }}</div>
    </div>
  </div>
</template>

<script>
import mime from 'mime'

export default {
  name: 'FileInputBig',
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    title: {
      type: [String, Number, Object],
      default: '',
    },
    placeholder: {
      type: [String, Object],
      default: '',
    },
    value: [File],
    hint: {
      type: [String, Object],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    vErrors: {
      type: Array,
      default() {
        return []
      },
    },
    isError: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: '.xlsx, .csv',
    },
    shouldTranslateTitle: {
      type: Boolean,
      default: false,
    },
    localizedTitle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      file: null,
      isFocused: false,
      currentType: '',
      selectedFile: null,
    }
  },
  computed: {
    locale() {
      let lang = this.$i18n?.locale ?? 'en'
      return lang === 'ua' ? 'uk' : lang
    },
    translatedTitle() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.title)
      }

      if (this.localizedTitle) {
        return this.title['uk'] ?? this.title
      }

      return this.title
    },
    translatedPlaceholder() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.placeholder)
      }

      if (this.localizedTitle && typeof this.placeholder === 'object') {
        return this.placeholder?.[this.locale] ?? this.placeholder?.['en'] ?? ''
      }

      return this.placeholder
    },
    translatedHint() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.hint)
      }

      if (this.localizedTitle) {
        return this.hint['uk'] ?? this.hint
      }

      return this.hint
    },
  },
  methods: {
    setFocused() {
      this.isFocused = true
    },
    setUnfocused() {
      this.isFocused = false
    },
    handleDrop(e) {
      this.isFocused = false
      const files = e.dataTransfer?.files || []

      if (!this.accept.includes(mime.getExtension(files[0].type))) {
        return
      }

      this.selectedFile = files[0]
			this.$emit('update:value', this.selectedFile)
    },
    processInput(e) {
      const files = e.target.files || e.dataTransfer.files

      this.selectedFile = files[0]
      this.$emit('update:value', this.selectedFile)
    },
    copyToClipboard() {
      if (!navigator.clipboard) {
        return
      }

      navigator.clipboard.writeText(this.text).then(
        () => {
          this.$emit('copied')
        },
        (err) => {
          console.error('Async: Could not copy text: ', err)
        }
      )
    },
    focus() {
      this.$refs.inputEl.focus()
    },
    togglePasswordShow() {
      this.currentType = this.currentType === 'password' ? 'text' : 'password'
    },
    emitFocus() {
      this.$emit('focus')
    },
    emitFocusOut() {
			this.$emit('focusout')
    },
  },
  emits: [
    'update',
    'copied',
    'focus',
    'focusout',
    'update:value',
  ],
}
</script>

<style lang="scss" scoped>
.file-input-big {
  width: 100%;
  position: relative;
  &__wrapper {
    position: relative;
    width: 100%;
  }

  &__title {
    font-weight: 400;
    font-size: 12px;
    line-height: 15px;
    color: #465a8b;
    padding: 2px 10px;
    position: absolute;
    top: 0;
    left: 15px;

    transform: translateY(-50%);
    background-color: #fff;

    z-index: 2;
  }

  &.filled & {
    &__el {
      border-style: solid;
      padding: 31px 14px;
      flex-direction: row;
      background: #64748B;
      border-radius: 19px;

      &-icon {
        width: 46px;
        height: 46px;

        display: flex;
        align-items: center;
        justify-content: center;

        background: #64748B;
        border-radius: 50%;
        margin-bottom: 0;
        margin-right: 19px;
        flex-shrink: 0;

        svg {
          path {
            stroke: #e0e6f2;
          }
        }
      }

      &-text {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        color: #f2f2f2;
        text-align: left;
      }
    }
  }

  &.focused & {
    &__el {
      border-color: #64748B;

      &-icon {
        svg {
          path {
            stroke: #64748B;
          }
        }
      }

      &-text {
        color: #64748B;
      }
    }
  }

  &__el {
    position: relative;
    width: 100%;
    background-color: transparent;
    padding: 45px 30px 60px 30px;
    outline: none;
    border: 1px dashed #64748B;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;

    &:focus,
    &:active {
      border-color: #64748B;
    }

    &:disabled {
      border-color: #eee;
      color: #d0d9ea;
    }

    input[type='file'] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      z-index: 0;
    }

    &-icon {
      pointer-events: none;
      margin-bottom: 30px;
    }

    &-text {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      text-align: center;
      color: #64748B;
    }

    &-hint {
      font-weight: 500;
      font-size: 10px;
      line-height: 16px;
      text-align: center;
      color: #b9c4d8;
    }
  }

  &__hint {
    margin-top: 4px;
    font-weight: 500;
    font-size: 10px;
    line-height: 14px;
    color: #b9c4d8;
  }
}
</style>

<style>
:-webkit-autofill {
  animation-name: onAutoFillStart;
}
:not(:-webkit-autofill) {
  animation-name: onAutoFillCancel;
}
@keyframes onAutoFillStart {
  from {
  }
  to {
  }
}
@keyframes onAutoFillCancel {
  from {
  }
  to {
  }
}
</style>
